// Pure helpers for the Files / Links / Tickets tab views and the
// conversation filter bar. Operate on the in-memory `messagesByChannel`
// bucket — no Supabase calls.
//
// Each `collect*` result is cached against the input array reference via
// a WeakMap so the same channel's scans run at most once per render
// cycle. The Files / Links / Tickets tab views each call their collector
// twice per render (Empty.if + children) — without this cache that's
// 6 full passes over the channel's message list per reactive update.

const _LINK_RE = /(https?:\/\/[^\s<>"`)\]]+)/g
const _TICKET_RE = /#([A-Z][A-Z0-9_-]{1,15}-\d+)/g
const _MENTION_RE = /@[a-zA-Z0-9._-]+/

const _attachmentsCache = new WeakMap()
const _linksCache = new WeakMap()
const _ticketsCache = new WeakMap()
const _authorsCache = new WeakMap()

const _EMPTY = Object.freeze([])

// Apply the active filter set to a flat message list. Returns the subset
// that matches every filter that's currently set (filters AND together).
export const applyFilters = (messages, filters) => {
  if (!filters) return messages || []
  const q = (filters.query || '').trim().toLowerCase()
  return (messages || []).filter(m => {
    if (filters.authorEmail && m.authorEmail !== filters.authorEmail) return false
    if (filters.hasAttachments && !(m.attachments && m.attachments.length)) return false
    if (filters.hasMentions && !_MENTION_RE.test(m.body || '')) return false
    if (q && !(m.body || '').toLowerCase().includes(q)) return false
    return true
  })
}

// Flatten every attachment in a channel's message bucket into a list with
// the originating message id + author + timestamp attached. Used by FilesView.
export const collectAttachments = (messages) => {
  if (!messages || !messages.length) return _EMPTY
  const cached = _attachmentsCache.get(messages)
  if (cached) return cached
  const out = []
  for (const m of messages) {
    if (!m.attachments || !m.attachments.length) continue
    for (const a of m.attachments) {
      out.push({
        ...a,
        messageId: m.id,
        authorEmail: m.authorEmail,
        createdAt: m.createdAt
      })
    }
  }
  _attachmentsCache.set(messages, out)
  return out
}

// Extract every URL from a channel's messages, deduped by URL with the
// most recent occurrence's metadata.
export const collectLinks = (messages) => {
  if (!messages || !messages.length) return _EMPTY
  const cached = _linksCache.get(messages)
  if (cached) return cached
  const seen = new Map()
  for (const m of messages) {
    if (!m.body) continue
    _LINK_RE.lastIndex = 0
    let match
    while ((match = _LINK_RE.exec(m.body)) !== null) {
      const url = match[1]
      const prev = seen.get(url)
      if (!prev || new Date(m.createdAt) > new Date(prev.createdAt)) {
        seen.set(url, {
          url,
          messageId: m.id,
          authorEmail: m.authorEmail,
          createdAt: m.createdAt,
          body: m.body
        })
      }
    }
  }
  const out = [...seen.values()].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  _linksCache.set(messages, out)
  return out
}

// Extract every #TICKET-123 reference from a channel's messages, similar
// to collectLinks. Tickets format: 1+ uppercase letters, optional digits/
// dashes, then `-` and a numeric id.
export const collectTickets = (messages) => {
  if (!messages || !messages.length) return _EMPTY
  const cached = _ticketsCache.get(messages)
  if (cached) return cached
  const out = []
  for (const m of messages) {
    if (!m.body) continue
    _TICKET_RE.lastIndex = 0
    let match
    while ((match = _TICKET_RE.exec(m.body)) !== null) {
      out.push({
        ref: match[1],
        messageId: m.id,
        authorEmail: m.authorEmail,
        createdAt: m.createdAt,
        body: m.body
      })
    }
  }
  out.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  _ticketsCache.set(messages, out)
  return out
}

// Distinct authors in a channel — used by the filter bar's author dropdown.
export const collectAuthors = (messages) => {
  if (!messages || !messages.length) return _EMPTY
  const cached = _authorsCache.get(messages)
  if (cached) return cached
  const seen = new Set()
  const out = []
  for (const m of messages) {
    if (!m.authorEmail || seen.has(m.authorEmail)) continue
    seen.add(m.authorEmail)
    out.push(m.authorEmail)
  }
  _authorsCache.set(messages, out)
  return out
}
